import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// CRA 3's Jest transformer treats `.cjs` files as static assets, so use the
// package's official browser bundle (`.js`) for both the app and its tests.
const spreadsheetWriter = require('write-excel-file/bundle/write-excel-file.min.js');

function resolveDefaultExport(moduleValue) {
  let resolved = moduleValue;
  while (resolved && typeof resolved !== 'function' && resolved.default !== resolved) {
    resolved = resolved.default;
  }
  return resolved;
}

const writeXlsxFile = resolveDefaultExport(spreadsheetWriter);

export const MAX_SPREADSHEET_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_SPREADSHEET_ROWS = 100000;

export class SpreadsheetError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'SpreadsheetError';
    this.code = code;
  }
}

function byteLength(value) {
  if (value == null) return 0;
  if (typeof value.byteLength === 'number') return value.byteLength;
  if (typeof value.length === 'number') return value.length;
  return 0;
}

export function parseSpreadsheetBuffer(buffer, options = {}) {
  const size = byteLength(buffer);
  const maxBytes = options.maxBytes || MAX_SPREADSHEET_FILE_BYTES;
  const maxRows = options.maxRows || MAX_SPREADSHEET_ROWS;

  if (!size) {
    throw new SpreadsheetError('The selected spreadsheet is empty.', 'EMPTY_FILE');
  }
  if (size > maxBytes) {
    throw new SpreadsheetError(
      `The selected spreadsheet exceeds the ${Math.round(maxBytes / 1024 / 1024)} MB limit.`,
      'FILE_TOO_LARGE'
    );
  }

  let workbook;
  try {
    workbook = XLSX.read(buffer, {
      type: 'array',
      cellDates: true,
      cellFormula: false,
      cellHTML: false,
      cellNF: false,
      dense: true,
    });
  } catch (_) {
    throw new SpreadsheetError(
      'The selected spreadsheet is corrupted or uses an unsupported format.',
      'INVALID_WORKBOOK'
    );
  }

  const sheetName = workbook.SheetNames && workbook.SheetNames[0];
  const sheet = sheetName && workbook.Sheets[sheetName];
  if (!sheet) {
    throw new SpreadsheetError('The selected spreadsheet contains no worksheets.', 'EMPTY_WORKBOOK');
  }

  let rows;
  try {
    rows = XLSX.utils.sheet_to_json(sheet, {
      raw: true,
      blankrows: false,
    });
  } catch (_) {
    throw new SpreadsheetError('The first worksheet could not be read.', 'INVALID_WORKSHEET');
  }

  if (!rows.length) {
    throw new SpreadsheetError('The first worksheet contains no data rows.', 'EMPTY_WORKSHEET');
  }
  if (rows.length > maxRows) {
    throw new SpreadsheetError(
      `The spreadsheet contains more than ${maxRows.toLocaleString()} data rows.`,
      'TOO_MANY_ROWS'
    );
  }

  return rows;
}

export function readSpreadsheetFile(file, options) {
  if (!file) {
    return Promise.reject(new SpreadsheetError('Please select a spreadsheet.', 'NO_FILE'));
  }

  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer().then((buffer) => parseSpreadsheetBuffer(buffer, options));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(parseSpreadsheetBuffer(reader.result, options));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(
      new SpreadsheetError('The selected spreadsheet could not be read.', 'FILE_READ_FAILED')
    );
    reader.readAsArrayBuffer(file);
  });
}

function reportCellValue(value) {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'v')) {
    return value.v;
  }
  return value == null ? '' : value;
}

function safeCsvValue(value) {
  const normalized = reportCellValue(value);
  if (normalized instanceof Date) return normalized.toISOString();
  if (typeof normalized === 'string' && /^[=+\-@]/.test(normalized)) {
    return `'${normalized}`;
  }
  return normalized;
}

export function createCsvContent(rows) {
  const normalizedRows = (rows || []).map((row) =>
    (row || []).map((value) => safeCsvValue(value))
  );
  return `\uFEFF${Papa.unparse(normalizedRows, { newline: '\r\n' })}`;
}

function excelColor(rgb) {
  const color = String(rgb || '').replace(/^#/, '').toUpperCase();
  if (color.length === 8) return `#${color.slice(2)}`;
  if (color.length === 6) return `#${color}`;
  return undefined;
}

function excelStyle(style, value) {
  if (!style) return undefined;
  const converted = {};

  if (style.font) {
    if (style.font.bold) converted.fontWeight = 'bold';
    if (style.font.italic) converted.fontStyle = 'italic';
    if (style.font.sz || style.font.size) converted.fontSize = style.font.sz || style.font.size;
    if (style.font.color && excelColor(style.font.color.rgb)) {
      converted.textColor = excelColor(style.font.color.rgb);
    }
  }
  if (style.fill && style.fill.fgColor && excelColor(style.fill.fgColor.rgb)) {
    converted.backgroundColor = excelColor(style.fill.fgColor.rgb);
  }
  if (style.alignment) {
    converted.align = style.alignment.horizontal;
    converted.alignVertical = style.alignment.vertical;
    converted.wrap = Boolean(style.alignment.wrapText);
  }
  if (style.numFmt) converted.format = style.numFmt;
  if (value instanceof Date && !converted.format) converted.format = 'yyyy-mm-dd';

  return converted;
}

function columnIndex(columnName) {
  return String(columnName).split('').reduce(
    (total, letter) => (total * 26) + letter.toUpperCase().charCodeAt(0) - 64,
    0
  ) - 1;
}

function applyMergeRanges(rows, ranges) {
  (ranges || []).forEach((range) => {
    const match = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i.exec(range);
    if (!match) throw new SpreadsheetError(`Invalid merge range: ${range}`, 'INVALID_MERGE');

    const startColumn = columnIndex(match[1]);
    const startRow = Number(match[2]) - 1;
    const endColumn = columnIndex(match[3]);
    const endRow = Number(match[4]) - 1;
    if (startRow !== endRow || endColumn < startColumn || !rows[startRow]) {
      throw new SpreadsheetError(`Unsupported merge range: ${range}`, 'INVALID_MERGE');
    }

    const sourceCell = rows[startRow][startColumn];
    rows[startRow][startColumn] = {
      ...(sourceCell && typeof sourceCell === 'object' ? sourceCell : { value: sourceCell }),
      columnSpan: endColumn - startColumn + 1,
    };
    for (let index = startColumn + 1; index <= endColumn; index += 1) {
      rows[startRow][index] = null;
    }
  });
}

export async function createXlsxBuffer(rows, options = {}) {
  if (typeof writeXlsxFile !== 'function') {
    throw new SpreadsheetError(
      'Spreadsheet export is unavailable in this browser.',
      'WRITER_UNAVAILABLE'
    );
  }
  const sheetData = (rows || []).map((sourceRow, rowIndex) => {
    const convertedRow = (sourceRow || []).map((sourceCell) => {
      const value = reportCellValue(sourceCell);
      const style = sourceCell && typeof sourceCell === 'object'
        ? excelStyle(sourceCell.s, value)
        : (value instanceof Date ? { format: 'yyyy-mm-dd' } : undefined);
      return style ? { value, ...style } : value;
    });
    const height = options.rowHeights && options.rowHeights[rowIndex];
    if (height && convertedRow.length) {
      const firstCell = convertedRow[0];
      convertedRow[0] = {
        ...(firstCell && typeof firstCell === 'object' ? firstCell : { value: firstCell }),
        height,
      };
    }
    return convertedRow;
  });

  applyMergeRanges(sheetData, options.merges);

  return writeXlsxFile(sheetData, {
    sheet: options.sheetName || 'Sheet1',
    columns: (options.columnWidths || []).map((width) => ({ width })),
  }).toBlob();
}

export const spreadsheetInternals = {
  applyMergeRanges,
  excelColor,
  excelStyle,
  reportCellValue,
  safeCsvValue,
};
